// D-043 / §14 — generated wallpapers.
//
// CSS gradients plus one SVG geometry layer, both derived from the wallpaper's name. Nothing
// binary, nothing fetched, resolution-independent — and because the whole thing is a pure
// function of a name, persisting the *name* is enough to restore the desktop exactly (§14).
import { createDraw, hashSeed } from './seed';

export interface WallpaperDef {
  id: string;
  label: string;
  /** Base hue, 0..359. */
  hue: number;
  /** `dark` wallpapers pair with the dark theme; `light` with light. */
  tone: 'dark' | 'light';
}

/** The catalog Settings → Personalization offers. Order is the order shown. */
export const WALLPAPERS: readonly WallpaperDef[] = [
  { id: 'hangar-dusk', label: 'Dusk', hue: 28, tone: 'dark' },
  { id: 'hangar-midnight', label: 'Midnight', hue: 224, tone: 'dark' },
  { id: 'hangar-pine', label: 'Pine', hue: 158, tone: 'dark' },
  { id: 'hangar-ember', label: 'Ember', hue: 8, tone: 'dark' },
  { id: 'hangar-slate', label: 'Slate', hue: 210, tone: 'dark' },
  { id: 'hangar-linen', label: 'Linen', hue: 36, tone: 'light' },
  { id: 'hangar-mist', label: 'Mist', hue: 196, tone: 'light' },
  { id: 'hangar-sand', label: 'Sand', hue: 44, tone: 'light' },
];

export const DEFAULT_WALLPAPER_ID = 'hangar-dusk';

/**
 * §11.7 — "Set as wallpaper" puts a *file* behind the desktop, and D-043's generator only knows
 * names. So the setting carries either a generated id or `file:<vfs path>`, and one prefix keeps
 * the two apart without a second setting to fall out of step with the first.
 *
 * The bytes are still not in the setting: what persists is the path, and the desktop resolves it
 * (D-169). A data URL in `settings` would put a multi-megabyte string in every settings write.
 */
export const WALLPAPER_FILE_PREFIX = 'file:';

export const wallpaperFileValue = (path: string): string => `${WALLPAPER_FILE_PREFIX}${path}`;

/** The VFS path behind a file wallpaper, or `null` for a generated one. */
export function wallpaperFilePath(value: string): string | null {
  return value.startsWith(WALLPAPER_FILE_PREFIX)
    ? value.slice(WALLPAPER_FILE_PREFIX.length)
    : null;
}

/** The `background` value for a resolved image — the picture, then the generated one behind it. */
export function wallpaperImageCss(objectUrl: string): string {
  return `url("${objectUrl}") center / cover no-repeat, ${wallpaperCss(DEFAULT_WALLPAPER_ID)}`;
}

export function wallpaperById(id: string): WallpaperDef {
  return (
    WALLPAPERS.find((w) => w.id === id) ?? {
      id,
      label: id,
      hue: hashSeed(id) % 360,
      tone: 'dark',
    }
  );
}

interface Palette {
  base: string;
  glowA: string;
  glowB: string;
  line: string;
}

function palette(def: WallpaperDef): Palette {
  const h2 = (def.hue + 46) % 360;
  return def.tone === 'dark'
    ? {
        base: `hsl(${def.hue} 22% 9%)`,
        glowA: `hsl(${def.hue} 58% 26%)`,
        glowB: `hsl(${h2} 46% 18%)`,
        line: `hsl(${def.hue} 40% 70% / 7%)`,
      }
    : {
        base: `hsl(${def.hue} 32% 92%)`,
        glowA: `hsl(${def.hue} 62% 84%)`,
        glowB: `hsl(${h2} 48% 88%)`,
        line: `hsl(${def.hue} 30% 30% / 8%)`,
      };
}

/**
 * The geometry layer: a handful of long, thin chevrons — deterministic from the name, and
 * quiet enough to sit behind windows without competing with them.
 */
export function wallpaperSvg(id: string): string {
  const def = wallpaperById(id);
  const draw = createDraw(hashSeed(`${id}:geometry`));
  const { line } = palette(def);

  const strokes: string[] = [];
  const count = 7;
  for (let i = 0; i < count; i += 1) {
    const y = draw.range(80, 920);
    const width = draw.range(240, 760);
    const x = draw.range(-120, 1000);
    const lift = draw.range(40, 160);
    strokes.push(
      `<path d="M${x} ${y} l${width / 2} -${lift} l${width / 2} ${lift}" fill="none" stroke="${line}" stroke-width="${draw.range(1, 4)}"/>`,
    );
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 1000" preserveAspectRatio="xMidYMid slice" width="1280" height="1000">` +
    strokes.join('') +
    `</svg>`
  );
}

/** A `background` shorthand value: geometry layer, two glows, then the base colour. */
export function wallpaperCss(id: string): string {
  const def = wallpaperById(id);
  const { base, glowA, glowB } = palette(def);
  const svg = `url("data:image/svg+xml,${encodeURIComponent(wallpaperSvg(id))}")`;
  return [
    `${svg} center / cover no-repeat`,
    `radial-gradient(120% 90% at 12% 8%, ${glowA} 0%, transparent 58%)`,
    `radial-gradient(110% 80% at 88% 96%, ${glowB} 0%, transparent 62%)`,
    base,
  ].join(', ');
}
